import { computedFrom } from "aurelia-binding";
import { autoinject, transient } from "aurelia-dependency-injection";
import { EventAggregator, Subscription } from "aurelia-event-aggregator";
import { Router } from "aurelia-router";
import { bindable } from "aurelia-templating";
import { RightShipQiRouterConstants } from "../../../../../configuration/rightshipqi-router.constants";
import { Claims } from "../../../../../constants/authentication/claims";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { RightshipEexiComplianceTypeEnum, VesselHeaderModel } from "../../../../../models/vessels/vessel.models";
import { UserClaimsService } from "../../../../../services/authentication/user-claims.service";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";
import { EntityStatusChangeModel, SignalFunctions } from "../../../../../services/basics/signal-functions";
import { ManageVesselService } from "../../../../../services/vessels/manage-vessel.service";
import { ClipboardCopyService } from "../../../../../utilities/clipboard-copy-service";
import { Reflection } from "../../../../../utilities/reflection";
import { CustomerService } from "../../../../../services/modules/customer-client.service";
import { CustomerModel } from "../../../../../models/customers/customer.models";
import { CustomerTypes } from "../../../../../constants/modules/customer.constants";

@autoinject
@transient()
export class VesselHeader {

    private _event: Subscription;

    @bindable()
    public vesselId: number;

    @bindable()
    public getVesselHeaderModel: (force: boolean) => Promise<VesselHeaderModel> = this.defaultGetVesselHeaderModel;

    @bindable()
    public isEditableVesselName: boolean = false;

    @bindable()
    public forceLoad: boolean = false;

    @bindable
    public showGrading: boolean = false;

    @bindable
    public showVesselComparisonLink: boolean = false;

    @bindable
    public redirectToVessel: boolean = true;

    @bindable
    public showDwtInfo: boolean = true;

    @bindable
    public signalRGrouping: boolean = true;

    @bindable
    public showCiiRating: boolean;

    public maskData: boolean = false;
    public model: VesselHeaderModel;
    public canEditVesselDetails: boolean;
    public canRequestRightShipInspection: boolean;
    public canAccessVesselComparison: boolean;
    public showEexiCompliantNotification: boolean;
    public isCustomer: boolean;
    public canShowSafetyScore: boolean;
    public customer: CustomerModel;

    @computedFrom(
        Reflection.getPropertyPath<VesselHeader>(v => v.model.hideSafetyScore),
        Reflection.getPropertyPath<VesselHeader>(v => v.canShowSafetyScore)
    )
    get showSafetyScore(): boolean {
        return this.canShowSafetyScore && !this.model.hideSafetyScore;
    }

    @computedFrom(Reflection.getPropertyPath<VesselHeader>(v => v.customer))
    get showVesselHealthCheckBanner(): boolean {

        //Show Vessel Health Check banner if Owners/Managers
        if (this.customer) {
            return this.customer.customerType == CustomerTypes.ShipManager || this.customer.customerType == CustomerTypes.ShipOwner;
        }
    }

    @computedFrom(
        Reflection.getPropertyPath<VesselHeader>(v => v.model.isSanctioned),
        Reflection.getPropertyPath<VesselHeader>(v => v.model.isInGeoPoliticalZone),
        Reflection.getPropertyPath<VesselHeader>(v => v.model.hasGeopoliticalConflict),
        Reflection.getPropertyPath<VesselHeader>(v => v.model.isFlagFake)
    )
    get isSanctionedOrGeopoliticalZoneOrFlagFake() {
        if (this.model.isSanctioned || this.model.isInGeoPoliticalZone || this.model.hasGeopoliticalConflict || this.model.isFlagFake) {
            return true;
        }
    }

    @computedFrom(
        Reflection.getPropertyPath<VesselHeader>(v => v.model.isEexiCompliant),
        Reflection.getPropertyPath<VesselHeader>(v => v.model.eexiComplianceTypeId),
        Reflection.getPropertyPath<VesselHeader>(v => v.showEexiCompliantNotification))
    public get showEexiWarning() {
        return this.model.isEexiCompliant != null && this.model.eexiComplianceTypeId != null && this.showEexiCompliantNotification && (this.model.isEexiCompliant == false || this.model.eexiComplianceTypeId == RightshipEexiComplianceTypeEnum.Evdi);
    }

    @computedFrom(Reflection.getPropertyPath<VesselHeader>(v => v.model.vesselId))
    public get vesselUrl() {
        return this.router.generate(RightShipQiRouterConstants.RouteNames.VesselDetails, {
            id: this.model.vesselId,
        });
    }

    constructor(private vesselService: ManageVesselService, private clipboardCopyService: ClipboardCopyService,
        userClaimsService: UserClaimsService, private router: Router,
        private manageVesselService: ManageVesselService, private customerService: CustomerService,
        private signalClientService: SignalClientService, private eventAggregator: EventAggregator) {
        this.canEditVesselDetails = userClaimsService.hasPermission(Claims.EditVesselDetails);
        this.canRequestRightShipInspection = userClaimsService.hasPermission(Claims.CanRequestRightShipInspection);
        this.canAccessVesselComparison = userClaimsService.hasPermission(Claims.CanAccessVesselComparison);
        this.showEexiCompliantNotification = userClaimsService.hasPermission(Claims.ShowEEXIComplianceNotification);
        this.isCustomer = !userClaimsService.hasPermission(Claims.RightShipUser);
        this.canShowSafetyScore = userClaimsService.hasPermission(Claims.ShowSafetyScore);
    }

    attached() {
        this.customerService.getCurrentCustomer().then(customerResponse => {
            this.customer = customerResponse.customers[0];
        });
        this._event = this.eventAggregator.subscribe("refresh-vessel-header", this.refreshRating);
    }

    detached() {
        if (!this.maskData) {
            this.signalClientService.unsubscribe(SignalFunctions.systemRuleChanged, this.refreshRating);
        }
        if (this.signalRGrouping) {
            this.signalClientService.unsubscribeToGroup(SignalFunctions.entityStatusChanged, SignalFunctions.entityTypeGroup(EntityTypes.RightShipInspection), this.rsInspectionRefresh);
        }
        else {
            this.signalClientService.unsubscribe(SignalFunctions.entityStatusChanged, this.rsInspectionRefresh);
        }

        this._event?.dispose();
    }

    private refreshRating = async (param) => {
        if (param && param.vesselIds && param.vesselIds.filter(x => x == this.vesselId).length >= 1) {
            this.model = await this.getVesselHeaderModel(true);
        }
    }

    private rsInspectionRefresh = async (param: EntityStatusChangeModel) => {
        if (param && param.vesselId && param.vesselId == this.vesselId) {
            this.model = await this.getVesselHeaderModel(true);
        }
    }

    vesselIdChanged() {
        if (this.vesselId) {
            const promise1 = this.getVesselHeaderModel(this.forceLoad);
            const promise2 = this.manageVesselService.getMaskDataForVessel(this.vesselId);

            Promise.all([promise1, promise2]).then(results => {

                //Is freemium
                this.maskData = results[1];

                this.model = results[0];
                if (!this.maskData) {
                    this.signalClientService.subscribe(SignalFunctions.systemRuleChanged, this.refreshRating);
                }
            });
        }
    }

    signalRGroupingChanged() {
        if (this.signalRGrouping) {
            this.signalClientService.subscribeToGroup(SignalFunctions.entityStatusChanged, SignalFunctions.entityTypeGroup(EntityTypes.RightShipInspection), this.rsInspectionRefresh);
        }
        else {
            this.signalClientService.subscribe(SignalFunctions.entityStatusChanged, this.rsInspectionRefresh);
        }
    }

    async defaultGetVesselHeaderModel(force: boolean): Promise<VesselHeaderModel> {
        if (this.vesselId) {
            try {
                return await this.vesselService.getVesselHeader(this.vesselId, force);
            } catch {
                console.error("Could not load vessel header vessel ID " + this.vesselId);
            }
        }
    }

    copy(str: string) {
        this.clipboardCopyService.copy(str);
    }

    navigateToComparisonPage() {
        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.VesselComparison, { vesselId: this.vesselId });
    }
}